import { useTranslations } from 'next-intl';

import { PositionHistory } from '@flipster/apis/v2/history/types';
import { InstrumentApi } from '@flipster/apis/v2/snapshot/types';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Badge } from '@flipster/react/design-system/badge';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import TrendIcon from '@flipster/react/icon/generated/TrendIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { CardListItem as CardItem } from '@flipster/ui/CardList';
import CircleInfoFillIcon from '@flipster/ui/assets/svgs/circle-info-fill.svg';
import ShareIcon from '@flipster/ui/assets/svgs/share.svg';
import { FeePrecision, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import webviewHandler from '@flipster/utils/webview-handler';

import { LeverageBadge, OrderSideBadge } from '../../../misc/badge';
import { SymbolDisplay } from '../../../misc/display';
import { Pnl } from '../../../shared/pnl';
import { PnlShareModal } from '../../../shared/pnl-share-modal';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { actualExecFeeValueStyles, originalFeeValueStyles } from './pnl-history-modal';
import { useFeeCalculations } from './use-fee-calculations';

interface Props {
  item: PositionHistory;
}

const PositionCard = ({ item }: Props) => {
  const overlay = useOverlay();
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const symbolInfo = instrumentsKeyBySymbol?.[item.symbol];

  const realizedPnl = item.netRealizedPnl;
  const realizedPnlPct = item.netRealizedPnlPct;

  return (
    <CardItem>
      <header
        className={flex({
          gap: 8,
          pos: 'relative',
          pb: 16,
          _before: {
            content: '""',
            pos: 'absolute',
            right: '-16px',
            bottom: 0,
            left: '-16px',
            h: 1,
            bgColor: 'border.default.level-3',
          },
        })}
      >
        <SymbolDisplay
          symbol={item.symbol}
          mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Title/XS' }} />}
        />
        <div className={flex({ flex: 1, gap: 4 })}>
          <OrderSideBadge orderSide={item.side} />
          <LeverageBadge leverage={item.initLeverage} marginType={item.isCrossMargin ? 'Cross' : 'Isolated'} />
          {item.boostApplied && (
            <Badge leadingIcon={<TrendIcon />} color='promotion'>
              <FormattedMessage id='Autogen_common.r251126.s_Boost' />
            </Badge>
          )}
        </div>
        <ShareIcon
          data-test-id='share-button'
          className={css({ color: 'content.default.level-3', cursor: 'pointer' })}
          onClick={() => {
            const pnlData = {
              positionId: item.positionId,
              symbol: item.symbol,
              side: item.side,
              entryPrice: item.avgOpenPrice,
              closePrice: item.avgClosePrice,
              pnl: realizedPnl,
              pnlPercentage: realizedPnlPct,
              initLeverage: item.initLeverage,
            };

            webviewHandler.postMessage({ action: 'link', params: { page: 'sharePerformance', data: pnlData } }, () =>
              overlay.open(({ isOpen, close, unmount }) => (
                <PnlShareModal
                  isOpen={isOpen}
                  onClose={close}
                  position={{ type: 'perpetual', status: 'closed', evaluationPrice: item.avgClosePrice, ...pnlData }}
                  onExit={unmount}
                />
              )),
            );
          }}
        />
      </header>
      <PnlSection item={item} quoteCurrency={symbolInfo?.quoteCurrency} />
      <dl
        className={flex({
          gap: 4,
          justify: 'between',
          wrap: 'wrap',
          p: '16px 0 12px',
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderColor: 'border.default.level-1',
        })}
      >
        <dt className={dtStyle}>
          <FormattedMessage id='Autogen_common.r251126.s_Quantity' />
        </dt>
        <dd className={ddStyle}>
          <Formatted.Number value={item.qty} />
        </dd>
        <dt className={dtStyle}>
          <FormattedMessage id='Autogen_common.r251126.s_Entry_price' />
        </dt>
        <dd className={ddStyle}>
          <Formatted.Number
            value={item.avgOpenPrice}
            precision={symbolInfo?.pricePrecision}
            suffix={symbolInfo?.quoteCurrency}
            toFixed
          />
        </dd>
        <dt className={dtStyle}>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Avg_closing_price' />
          </span>
          <Tooltip placement='bottom'>
            <Tooltip.Trigger css={{ display: 'inline-flex' }}>
              <CircleInfoFillIcon />
            </Tooltip.Trigger>
            <Tooltip.Content>
              <FormattedMessage id='Autogen_common.r251126.s_The_average_price_at_which_the_position_was_closed' />
            </Tooltip.Content>
          </Tooltip>
        </dt>
        <dd className={ddStyle}>
          <Formatted.Number
            value={item.avgClosePrice}
            precision={symbolInfo?.pricePrecision}
            suffix={symbolInfo?.quoteCurrency}
            toFixed
          />
        </dd>
      </dl>
      <ul className={flex({ gap: 8 })}>
        <li className={liStyle}>
          <span className={titleStyle}>
            <FormattedMessage id='Autogen_common.r251126.s_Open' />
          </span>
          <span className={dateStyle}>
            <Datetime value={item.openTime} />
          </span>
        </li>
        <li className={liStyle}>
          <span className={titleStyle}>
            <FormattedMessage id='Autogen_common.r251126.s_Close' />
          </span>
          <span className={dateStyle}>
            <Datetime value={item.closeTime} />
          </span>
        </li>
      </ul>
    </CardItem>
  );
};

export default PositionCard;

const dtStyle = flex({
  flex: '50%',
  align: 'center',
  gap: 4,
  textStyle: 'Text/M/Regular',
  color: 'content.default.level-2',
});

const ddStyle = flex({ gap: 4 });

const liStyle = flex({
  justify: 'center',
  align: 'flex-start',
  flex: 1,
  direction: 'column',
  gap: 4,
  p: 8,
  bgColor: 'background.default.level-3',
  rounded: 4,
});

const titleStyle = css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' });
const dateStyle = css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' });

interface PnlSectionProps {
  item: PositionHistory;
  quoteCurrency: InstrumentApi['quoteCurrency'] | undefined;
}

const PnlSection = ({ item, quoteCurrency = 'USDT' }: PnlSectionProps) => {
  const t = useTranslations();

  const realizedPnl = item.netRealizedPnl;
  const realizedPnlPct = item.netRealizedPnlPct;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
  const currency = quoteCurrency;

  const {
    feeCalculations,
    hasTradingFeeBonusRedemption,
    tradingFeeBonusRedemptionText,
    hasFundingFeeBonusRedemption,
    fundingFeeBonusRedemptionText,
  } = useFeeCalculations(item);

  return (
    <div className={stack({ gap: 4, p: '16px 0 12px' })}>
      <dl
        className={stack({
          gap: 4,
          '& > div': { display: 'flex', justifyContent: 'space-between' },
          '& dt': {
            flex: 1,
            textStyle: 'Text/S/regular',
            color: 'content.default.level-2',
          },
          '& dd': {
            flex: 1,
            textStyle: 'Text/S/regular',
            textAlign: 'right',
          },
        })}
      >
        <div>
          <dt className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5!' })}>
            {t('Autogen_common.r251126.s_Realized_PnL_ROI')}
          </dt>
          <dd className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            <Pnl number={realizedPnl} percent={realizedPnlPct}>
              <Pnl.Number currency={currency} /> <Pnl.Percent />
            </Pnl>
          </dd>
        </div>
      </dl>
      <dl
        className={stack({
          gap: 4,
          '& > div': { display: 'flex', justifyContent: 'space-between' },
          '& dt': {
            flex: 1,
            textStyle: 'Text/S/regular',
            color: 'content.default.level-2',
          },
          '& dd': {
            flex: 1,
            textAlign: 'right',
          },
        })}
      >
        <div>
          <dt>{t('Common.r251107.Trade_pnl')}</dt>
          <dd>
            <Formatted.Number
              value={item.grossRealizedPnl}
              precision={quoteCurrencyPrecision}
              suffix={currency}
              toFixed
              withColor
              withPlus
            />
          </dd>
        </div>
        <div>
          <dt>{t('Common.r251107.Funding_fee')}</dt>
          <dd>
            {hasFundingFeeBonusRedemption && (
              <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                <Formatted.Number
                  value={feeCalculations.originalFundingFee}
                  precision={FeePrecision}
                  suffix={currency}
                  zeroString='0.00'
                  toFixed
                  css={originalFeeValueStyles}
                />
                <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
              </div>
            )}
            <Formatted.Number
              value={feeCalculations.actualFundingFee}
              precision={FeePrecision}
              suffix={currency}
              zeroString='0.00'
              toFixed
              withColor
              withPlus
            />
            {fundingFeeBonusRedemptionText && (
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {fundingFeeBonusRedemptionText}
              </p>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('Common.r251107.Trading_fee')}</dt>
          <dd>
            {hasTradingFeeBonusRedemption && (
              <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                <Formatted.Number
                  value={feeCalculations.originalTradingFee}
                  precision={FeePrecision}
                  suffix={currency}
                  zeroString='0.00'
                  toFixed
                  css={originalFeeValueStyles}
                />
                <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
              </div>
            )}
            <Formatted.Number
              value={feeCalculations.actualTradingFee}
              precision={FeePrecision}
              suffix={currency}
              zeroString='0.00'
              toFixed
              withColor
              withPlus
              css={actualExecFeeValueStyles}
            />
            {tradingFeeBonusRedemptionText && (
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {tradingFeeBonusRedemptionText}
              </p>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
};
